import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowLeft } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import { getVATBreakdown } from '../utils/vatCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to resolve image URLs
const resolveImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/120';
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const CartPage = () => {
  const navigate = useNavigate();
  const { language, cart, updateCartItem, removeFromCart, siteSettings } = useStore();
  const t = translations[language];

  const subtotalTTC = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const { htva: subtotalHTVA, tva: totalTVA } = getVATBreakdown(subtotalTTC);
  const freeShippingThreshold = siteSettings?.freeShippingThreshold || 75;
  const shippingCost = subtotalTTC >= freeShippingThreshold ? 0 : (siteSettings?.shippingCost || 5.95);
  const total = subtotalTTC + shippingCost;
  const amountToFreeShipping = freeShippingThreshold - subtotalTTC;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="cart-page">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.cart.empty}</h2>
          <p className="text-gray-500 mb-6">
            {language === 'fr'
              ? 'Votre panier est vide. Découvrez nos collections !'
              : 'Uw winkelwagen is leeg. Ontdek onze collecties!'}
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t.cart.continueShopping}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="cart-page">
      {/* Header */}
      <div className="bg-amber-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-white">{t.cart.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Free Shipping Progress */}
        {subtotalTTC < freeShippingThreshold && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-800">
              {language === 'fr'
                ? `Plus que €${amountToFreeShipping.toFixed(2)} pour la livraison gratuite !`
                : `Nog €${amountToFreeShipping.toFixed(2)} voor gratis verzending!`}
            </p>
            <div className="mt-2 h-2 bg-amber-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-600 rounded-full transition-all"
                style={{ width: `${Math.min((subtotalTTC / freeShippingThreshold) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              {cart.map((item, index) => (
                <div
                  key={`${item.product_id}-${item.size}-${item.color}`}
                  className={`flex gap-4 p-4 ${index > 0 ? 'border-t' : ''}`}
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img
                    src={resolveImageUrl(item.image)}
                    alt={item.name}
                    className="w-24 h-32 object-cover rounded"
                  />
                  <div className="flex-1">
                    <Link
                      to={`/product/${item.product_id}`}
                      className="font-medium text-gray-900 hover:text-amber-700"
                    >
                      {item.name}
                    </Link>
                    <div className="text-sm text-gray-500 mt-1">
                      {t.products.size}: {item.size} | {t.products.color}: {item.color}
                    </div>
                    <div className="text-lg font-bold text-amber-900 mt-2">
                      €{(item.price * item.quantity).toFixed(2)} <span className="text-xs font-normal text-gray-500">TTC</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {(() => {
                        const { htva, tva } = getVATBreakdown(item.price * item.quantity);
                        return `(€${htva.toFixed(2)} HTVA + €${tva.toFixed(2)} TVA)`;
                      })()}
                    </div>

                    {/* Quantity & Remove */}
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center border rounded">
                        <button
                          onClick={() =>
                            updateCartItem(
                              item.product_id,
                              item.size,
                              item.color,
                              item.quantity - 1
                            )
                          }
                          className="p-1.5 hover:bg-gray-100"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="px-3 py-1 min-w-[2.5rem] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateCartItem(
                              item.product_id,
                              item.size,
                              item.color,
                              item.quantity + 1
                            )
                          }
                          className="p-1.5 hover:bg-gray-100"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() =>
                          removeFromCart(item.product_id, item.size, item.color)
                        }
                        className="text-red-500 hover:text-red-600 flex items-center gap-1 text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t.cart.remove}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/shop"
              className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 mt-4"
            >
              <ArrowLeft className="w-5 h-5" />
              {t.cart.continueShopping}
            </Link>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {language === 'fr' ? 'Récapitulatif' : 'Overzicht'}
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{language === 'fr' ? 'Sous-total HTVA' : 'Subtotaal excl. BTW'}</span>
                  <span className="font-medium">€{subtotalHTVA.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{language === 'fr' ? 'TVA (21%)' : 'BTW (21%)'}</span>
                  <span className="font-medium">€{totalTVA.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.cart.subtotal} TTC</span>
                  <span className="font-medium">€{subtotalTTC.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.cart.shipping}</span>
                  <span className="font-medium">
                    {shippingCost === 0 ? (
                      <span className="text-green-600">{t.cart.freeShipping}</span>
                    ) : (
                      `€${shippingCost.toFixed(2)}`
                    )}
                  </span>
                </div>
                <div className="border-t pt-4 flex justify-between">
                  <span className="text-lg font-bold">{t.cart.total} TTC</span>
                  <span className="text-lg font-bold text-amber-900">
                    €{total.toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white py-4 rounded-lg font-semibold transition-colors"
                data-testid="checkout-btn"
              >
                {t.cart.checkout}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                {language === 'fr'
                  ? 'Paiement sécurisé par QR Code bancaire'
                  : 'Veilige betaling via bank QR-code'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
